import DOMPurify from "dompurify";

/**
 * Todo cuerpo HTML de un correo recibido es contenido no confiable, sin
 * excepción, sin importar el remitente (CLAUDE.md §3.4). Esta sanitización
 * es la primera capa de defensa — la segunda es renderizar el resultado en
 * un <iframe sandbox> sin allow-same-origin + allow-scripts juntos (ver
 * components/EmailBody.tsx), para que un bypass acá no alcance el DOM real
 * de la app.
 */
export function sanitizeEmailHtml(rawHtml: string): string {
  return DOMPurify.sanitize(rawHtml, {
    FORBID_TAGS: ["script", "iframe", "object", "embed", "style", "link", "meta", "base", "form"],
    FORBID_ATTR: ["srcdoc"],
    FORBID_CONTENTS: ["script", "style"],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
    KEEP_CONTENT: true,
  });
}

/**
 * Bloquea la carga automática de imágenes remotas (protección contra
 * tracking pixels, CLAUDE.md §3.4) moviendo `src` a `data-blocked-src` en
 * cada `<img>`. Correr DESPUÉS de sanitizeEmailHtml. `loadImages()` revierte
 * esto cuando el usuario decide cargar las imágenes de un correo puntual.
 */
export function blockRemoteImages(sanitizedHtml: string): string {
  const doc = new DOMParser().parseFromString(sanitizedHtml, "text/html");
  doc.querySelectorAll("img[src]").forEach((img) => {
    const src = img.getAttribute("src");
    if (src && !src.startsWith("data:")) {
      img.setAttribute("data-blocked-src", src);
      img.removeAttribute("src");
    }
  });
  return doc.body.innerHTML;
}

export function loadImages(htmlWithBlockedImages: string): string {
  const doc = new DOMParser().parseFromString(htmlWithBlockedImages, "text/html");
  doc.querySelectorAll("img[data-blocked-src]").forEach((img) => {
    const src = img.getAttribute("data-blocked-src");
    if (src) img.setAttribute("src", src);
    img.removeAttribute("data-blocked-src");
  });
  return doc.body.innerHTML;
}

export function hasBlockedImages(htmlWithBlockedImages: string): boolean {
  return htmlWithBlockedImages.includes("data-blocked-src=");
}

/**
 * HTML de correo → texto plano, para precompletar la cita al Responder/
 * Reenviar (Fase 4) — el compose de esta app es solo texto plano, no HTML
 * (ver ComposePane.tsx). Mismo patrón ya usado en blockRemoteImages/
 * loadImages: se sanitiza, se parsea con DOMParser en un documento que
 * nunca se inserta en el DOM real, y se lee `.textContent` — no hay riesgo
 * de XSS porque nunca se renderiza, solo se lee como string.
 */
export function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(sanitizeEmailHtml(html), "text/html");
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  doc.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, h5, h6, blockquote").forEach((el) => {
    el.append("\n");
  });
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

const CONTROL_CHARS_REGEXP = new RegExp("[\\u0000-\\u001f\\u007f]", "g");

/**
 * Texto de headers (From/Subject/Reply-To) — nunca se interpreta como HTML,
 * solo se muestra como texto plano (CLAUDE.md §3.4). React ya escapa texto
 * por defecto al interpolarlo en JSX; esta función saca caracteres de
 * control (incluye CR/LF) para los pocos lugares donde el texto viaja fuera
 * de JSX (ej. atributos title, alt) y podría usarse para inyectar líneas.
 */
export function sanitizeHeaderText(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(CONTROL_CHARS_REGEXP, "").trim();
}
